import React from 'react'
import {
  Box,
  ButtonPrimary,
  Heading,
  StackDivider,
  Text,
  VStack
} from 'front-end/src/components'
import { Trans } from '@lingui/macro'
import { FormRow } from 'front-end/src/pages/auth/self-signup/fields/FormRow'
import { InputControl } from 'formik-chakra-ui'
import { useFormikContext } from 'formik'
import {
  AllowedUserFieldsEnum,
  AllowedUserVerificationsFieldsSet
} from 'shared/accountNameConfig'
import { useTranslatedFieldsLabels } from 'front-end/src/pages/auth/self-signup/fields/useTranslatedFieldsLabels'
import { useVerificationFormFieldsContext } from 'front-end/src/pages/auth/verification-user-form-step/VerificationFormFieldsContext'
import { FIELDS_CUSTOM_INPUT, getUserFieldsProps } from '../AuthHelpers'
import { Alert, AlertIcon, AlertTitle } from '@chakra-ui/react'

type VerificationFormProps = {
  verificationLoading: boolean
}

export const VerificationForm = ({
  verificationLoading
}: VerificationFormProps) => {
  const { handleSubmit, isValid } = useFormikContext()
  const { fields } = useVerificationFormFieldsContext()

  const formConstructorFields = React.useMemo(() => {
    return fields.filter((field) =>
      AllowedUserVerificationsFieldsSet.has(
        field as string as AllowedUserFieldsEnum
      )
    )
  }, [fields])
  // we need to compute that constant on fly, cos translations need to be fetched before
  const FIELDS_LABELS = useTranslatedFieldsLabels()

  return (
    <form onSubmit={handleSubmit}>
      <Heading size="md">
        <Trans>Verification</Trans>
      </Heading>
      <Text mt="16px" mb="12px">
        {formConstructorFields.length > 0 ? (
          <Trans>
            We need to verify it's really you. Please answer the given fields
            precisely.
          </Trans>
        ) : (
          <Alert status="warning" flexWrap={['wrap', 'initial']} mt="24px">
            <AlertIcon />
            <AlertTitle mr="8px">
              <Trans>
                The verification fields was not provided. You will not be able
                to sign-in. Please contact an administrator.
              </Trans>
            </AlertTitle>
          </Alert>
        )}
      </Text>
      <VStack
        divider={<StackDivider bg="gray.200" h="1px" />}
        spacing={'10px'}
        align="stretch"
      >
        <Box>
          {formConstructorFields.map((field) => {
            return (
              <FormRow key={field}>
                {FIELDS_CUSTOM_INPUT[field] ? (
                  FIELDS_CUSTOM_INPUT[field](field)
                ) : (
                  <InputControl {...getUserFieldsProps(field, FIELDS_LABELS)} />
                )}
              </FormRow>
            )
          })}
        </Box>
      </VStack>
      <ButtonPrimary
        disabled={!isValid || formConstructorFields.length === 0}
        type="submit"
        isFullWidth
        mt="24px"
        isLoading={verificationLoading}
      >
        <Trans>Verify</Trans>
      </ButtonPrimary>
    </form>
  )
}
